import { styled } from "goober";
import { useState } from "react";
import { playClick } from "../../lib/sounds";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";

const Row = styled("div")`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const RetryAction = styled("button")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 9999px;
  border: none;
  background: transparent;
  color: var(--text-primary);
  cursor: pointer;
  &:hover {
    background-color: var(--alpha4);
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: 2px;
  }
`;

const RetryIcon = styled("span")`
  display: flex;

  &[data-spinning="true"] {
    animation: retry-spin 0.45s ease;
  }

  @keyframes retry-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export function ErrorMessage({
  message,
  createdAt,
  canRetry,
  onRetry,
}: {
  message: string;
  createdAt: number;
  canRetry: boolean;
  onRetry: () => void;
}) {
  // Expanded when the error just happened (arrived live while the user is
  // watching); collapsed when revisiting an older conversation.
  const [expanded, setExpanded] = useState(
    () => Date.now() - createdAt < 10_000
  );
  const [retrySpinKey, setRetrySpinKey] = useState(0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Row>
        <CollapsibleHeader onPress={() => setExpanded((prev) => !prev)}>
          <CollapsibleHeaderInner>
            <ToggleIconContainer aria-hidden>
              <DefaultIcon>
                <Icon
                  name="traffic-cone"
                  size={12}
                  color="var(--text-secondary)"
                />
              </DefaultIcon>
              <HoverIcon>
                <Icon
                  name={expanded ? "minus" : "plus"}
                  size={12}
                  color="var(--text-secondary)"
                />
              </HoverIcon>
            </ToggleIconContainer>
            <Text variant="text-small" color="var(--text-secondary)">
              Something went wrong
            </Text>
          </CollapsibleHeaderInner>
        </CollapsibleHeader>
        {canRetry && (
          <Tooltip content="Retry">
            <RetryAction
              type="button"
              aria-label="Retry"
              onMouseDown={(e) => {
                if (e.button !== 0) return;
                e.preventDefault();
                playClick();
                setRetrySpinKey((key) => key + 1);
                onRetry();
              }}
            >
              <RetryIcon key={retrySpinKey} data-spinning={retrySpinKey > 0}>
                <Icon name="rotate-cw" size={12} />
              </RetryIcon>
            </RetryAction>
          </Tooltip>
        )}
      </Row>
      <ExpandWrapper data-expanded={expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            <Text variant="text-small" color="var(--text-secondary)">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {message}
              </span>
            </Text>
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}
